import {
  PrinterTemplate,
  RemoveOrderItemEvent,
  Course,
  PrinterSeries,
  LOCALE,
  DEFAULT_TABLE_ABBREVIATION,
  SML_KITCHEN_DOCKET,
  LRG_KITCHEN_DOCKET,
  AllergensKey,
  ProductAllergens,
  Docket,
  DocketItem,
  IntegrationApps,
  DocketItemStatus,
  PrintingConfig,
  PrintInRed,
  SortingConfig,
  SortBy,
  Category,
  GroupDisplayStyle,
  GroupingConfig,
  GroupBy,
  NotifierConfig,
  NotifierDisplayStyle,
  PrinterProfile,
} from '@oolio-group/domain';
import { EscPos } from '@tillpos/xml-escpos-helper';
import { table, getBorderCharacters, TableUserConfig } from 'table';
import { Session } from '../../state/preferences';
import { dashDivider, divider } from './printDivider';
import { get } from 'lodash';
import { format } from 'date-fns';
import {
  getOnlineOrderDetail,
  getOnlineOrderType,
  isOnlineOrder,
} from './generateOnlineOrderDetail';
import { getOrderChannel } from './salesChannel';
import {
  kitchenDocketLabelTemplate,
  kitchenDocketTemplate,
  lrgKitchenDocketTemplate,
  smlkitchenDocketTemplate,
} from './xmlTemplates';
import { isDeselectDefaultOption } from '@oolio-group/client-utils';
import { consolidateOrderItems } from '../printerUtils';
const nameToTemplate: { [key: string]: string } = {
  [SML_KITCHEN_DOCKET]: smlkitchenDocketTemplate,
  [LRG_KITCHEN_DOCKET]: lrgKitchenDocketTemplate,
};

export interface GroupedDocketItems {
  header: string | undefined;
  printItems: DocketItem[];
  printHeaderInRed?: boolean;
}
export interface CoursePrintItems {
  courseName: string;
  priority: number;
  printItems: DocketItem[];
}

export type PrintableProductRow = [
  string | undefined,
  string | undefined,
  boolean | undefined,
];
export interface RemoveOrderItemDocketEvent extends RemoveOrderItemEvent {
  quantity?: number;
}
interface OrderItemsPrintDetails {
  groupedItems: GroupedPrintItems[];
  header?: string;
}
interface PrintableLineItems {
  product: string;
  note: string;
  modifiers: PrintableModifier[];
  printNoteInRed: boolean;
  comboItems?: PrintableLineItems[];
}

export interface PrintableModifier {
  text: string;
  printInRed: boolean;
}
interface GroupedPrintItems {
  items: PrintableLineItems[];
  subHeader?: string;
  allergens?: string;
  showSubDivider?: boolean;
  printSubHeaderInRed?: boolean;
}
type PartialPrintableKitchenOrder = (args: {
  docket: Docket;
  session: Session;
  template: PrinterTemplate;
  printItems: DocketItem[];
  hasVoidOrderItem?: boolean;
  translatedNowCourse: string;
  profileName?: string;
  locale?: LOCALE;
  printerSeries?: PrinterSeries;
  enableKitchenBuzzer?: boolean;
  singleItemPrinting?: boolean;
  currentItemNumber?: number;
  totalItems?: number;
  consolidationEnabled?: boolean;
  printConfig?: PrintingConfig;
  sortConfig?: SortingConfig;
  groupingConfig?: GroupingConfig;
  categories?: Category[];
  notifierConfig?: NotifierConfig;
  allProfiles?: PrinterProfile[];
}) => Buffer | undefined;

interface PrintInRedConfig {
  modifier: boolean;
  removal: boolean;
  notes: boolean;
  allergens: boolean;
}

function formatNameWithSeat(name?: string, seatName?: string): string {
  if (name) {
    const cleanName = name.trim();
    const cleanSeat = seatName?.trim();
    const dots = cleanName.length > 17 ? '.'.repeat(4) : '.'.repeat(7);
    return `${cleanName}${dots}${cleanSeat}`;
  }
  return '';
}

const NOTIFIERS_HEADER = 'OTHERS';

/**
 * Order details section has two columns and `n` row(s)
 */
//for text width 1
export const twoColumnLargeTextConfig: TableUserConfig = {
  columns: {
    0: { alignment: 'left', width: 9 },
    1: { alignment: 'right', width: 10, wrapWord: true },
  },
  border: getBorderCharacters('void'),
  columnDefault: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  drawHorizontalLine: () => {
    return false;
  },
};

//for text width 0
export const twoColumnConfig: TableUserConfig = {
  columns: {
    0: { alignment: 'left', width: 18 },
    1: { alignment: 'right', width: 20 },
  },
  border: getBorderCharacters('void'),
  columnDefault: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  drawHorizontalLine: () => {
    return false;
  },
};

export function tableOrderItemConfig(
  printerSeries: PrinterSeries | undefined,
): TableUserConfig {
  return {
    columns: {
      0: { width: 4, alignment: 'right' },
      1: {
        width: printerSeries === PrinterSeries.TM_U220 ? 13 : 15,
        alignment: 'left',
        wrapWord: true,
      },
    },
    border: getBorderCharacters('void'),
    drawHorizontalLine: () => false,
  };
}

export function tableComboOrderItemConfig(
  printerSeries?: PrinterSeries,
): TableUserConfig {
  return {
    columns: {
      0: {
        width: printerSeries === PrinterSeries.TM_U220 ? 25 : 30,
        alignment: 'left',
        wrapWord: true,
      },
    },
    border: getBorderCharacters('void'),
    columnDefault: {
      paddingLeft: 0,
      paddingRight: 0,
    },
    drawHorizontalLine: () => false,
  };
}

const tableOrderItemModifierConfig: (
  template?: PrinterTemplate,
) => TableUserConfig = template =>
  template?.name === LRG_KITCHEN_DOCKET
    ? {
        columns: {
          0: { width: 2, alignment: 'left' },
          1: {
            width: 12,
            alignment: 'left',
            wrapWord: true,
          },
        },
        border: getBorderCharacters('void'),
        columnDefault: {
          paddingLeft: 0,
          paddingRight: 1,
        },
        drawHorizontalLine: () => {
          return false;
        },
      }
    : {
        columns: {
          0: { width: 4, alignment: 'left' },
          1: {
            width: 25,
            alignment: 'left',
            wrapWord: true,
          },
        },
        border: getBorderCharacters('void'),
        columnDefault: {
          paddingLeft: 0,
          paddingRight: 1,
        },
        drawHorizontalLine: () => {
          return false;
        },
      };

const tableOrderItemNoteConfig: (
  template?: PrinterTemplate,
) => TableUserConfig = template =>
  template?.name === LRG_KITCHEN_DOCKET
    ? {
        columns: {
          0: { wrapWord: true },
        },
        border: getBorderCharacters('void'),
        drawHorizontalLine: () => {
          return false;
        },
      }
    : {
        columns: {
          0: { wrapWord: true },
        },
        border: getBorderCharacters('void'),
        drawHorizontalLine: () => {
          return false;
        },
      };

const generatePrintInRedConfig = (
  config?: PrintingConfig,
): PrintInRedConfig => {
  const enablePrintRed = config?.printInRed || false;
  const items = config?.printInRedFor || [];
  let enableModifier = false;
  let enableRemoval = false;
  let enableNotes = false;
  let enableAllergens = false;

  if (enablePrintRed) {
    items.forEach(e => {
      switch (e) {
        case PrintInRed.MODIFIERS:
          enableModifier = enablePrintRed;
          break;
        case PrintInRed.REMOVES:
          enableRemoval = enablePrintRed;
          break;
        case PrintInRed.NOTES:
          enableNotes = enablePrintRed;
          break;
        case PrintInRed.ALLERGIES:
          enableAllergens = enablePrintRed;
          break;
      }
    });
  }

  return {
    modifier: enableModifier,
    removal: enableRemoval,
    notes: enableNotes,
    allergens: enableAllergens,
  };
};

const sortDocketItems = (
  groupedItems: GroupedDocketItems[],
  sortConfig?: SortingConfig,
) => {
  const result: Record<string, DocketItem[]> = {};

  for (const group of groupedItems) {
    let sortedItems = group.printItems;

    if (sortConfig?.enabled) {
      sortedItems = [...sortedItems].sort((a, b) => {
        const isCreatedA =
          a.itemStatus === DocketItemStatus.CREATED ||
          a.itemStatus === DocketItemStatus.RECALLED;
        const isCreatedB =
          b.itemStatus === DocketItemStatus.CREATED ||
          b.itemStatus === DocketItemStatus.RECALLED;

        // CREATED items come first
        if (isCreatedA && !isCreatedB) return -1;
        if (!isCreatedA && isCreatedB) return 1;

        // Apply regular sort logic for the rest
        switch (sortConfig.sortBy) {
          case SortBy.SEAT:
            return (a.seatNumber ?? 0) - (b.seatNumber ?? 0);

          case SortBy.CATEGORY:
          case SortBy.SEQUENCE_NUMBER: {
            const aSort: [number, number] = [
              a.pagePriority ?? 0,
              a.priority ?? 0,
            ];
            const bSort: [number, number] = [
              b.pagePriority ?? 0,
              b.priority ?? 0,
            ];

            if (aSort[0] !== bSort[0]) return aSort[0] - bSort[0];
            return aSort[1] - bSort[1];
          }

          case SortBy.EXACT_CART_ORDER:
            return (a.createdAt ?? 0) - (b.createdAt ?? 0);

          default:
            return 0;
        }
      });
    }

    result[group.header || ''] = sortedItems;
  }

  return result;
};

const getGroupingStrategy = (
  groupingConfig?: GroupingConfig,
): GroupBy | undefined => {
  const isEnabled = groupingConfig?.enabled;
  if (isEnabled) {
    return groupingConfig.groupBy;
  }
  return undefined;
};

const getGroupingStyle = (
  groupingConfig?: GroupingConfig,
): GroupDisplayStyle => {
  const isEnabled = groupingConfig?.enabled;
  if (isEnabled) {
    return groupingConfig.displayStyle;
  }
  return GroupDisplayStyle.HEADING;
};

const groupBySeats = (printItems: DocketItem[]): GroupedDocketItems[] => {
  const grouped = printItems.reduce<Record<number | string, DocketItem[]>>(
    (acc, item) => {
      const key =
        item.seatNumber != null ? item.seatNumber : DEFAULT_TABLE_ABBREVIATION;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    },
    {} as Record<number | string, DocketItem[]>,
  );

  const groupedArray = Object.entries(grouped).map(([key, items]) => ({
    seat: key === DEFAULT_TABLE_ABBREVIATION ? null : Number(key),
    items,
  }));

  groupedArray.sort((a, b) => {
    if (a.seat === null) return -1;
    if (b.seat === null) return 1;
    return a.seat - b.seat;
  });
  return groupedArray.map(({ seat, items }) => ({
    header: seat != null ? `SEAT ${seat}` : DEFAULT_TABLE_ABBREVIATION,
    printItems: items,
  }));
};

const groupByCourses = (
  printItems: DocketItem[],
  translatedNowCourse: string,
): GroupedDocketItems[] => {
  const itemsWithCourse = printItems.reduce((courses, item) => {
    const courseName = item.course?.name || translatedNowCourse;
    const priority = item.course?.priority || -1;

    const existingIndex = courses.findIndex(c => c.courseName === courseName);
    if (existingIndex < 0) {
      courses.push({
        courseName,
        priority,
        printItems: [item],
      });
    } else {
      courses[existingIndex].printItems.push(item);
    }

    return courses;
  }, [] as { courseName: string; priority: number; printItems: DocketItem[] }[]);

  return itemsWithCourse
    .sort((a, b) => a.priority - b.priority)
    .map(group => ({
      header: group.courseName,
      printItems: group.printItems,
      printHeaderInRed: true,
    }));
};

const groupByCategory = (
  printItems: DocketItem[],
  categories: Category[],
): GroupedDocketItems[] => {
  const categoryMap: Record<string, GroupedDocketItems> = {};

  for (const item of printItems) {
    const categoryId = item.product?.category?.id;
    const category = categoryId
      ? categories.find(cat => cat.id === categoryId)
      : undefined;

    const key = category && categoryId ? categoryId : '';

    if (!categoryMap[key]) {
      categoryMap[key] = {
        header: category?.name,
        printItems: [],
      };
    }

    categoryMap[key].printItems.push(item);
  }

  return Object.values(categoryMap).sort((a, b) =>
    (a.header || '').localeCompare(b.header || ''),
  );
};

export const getPrintableKitchenOrder: PartialPrintableKitchenOrder = ({
  docket,
  printItems,
  session,
  hasVoidOrderItem,
  template,
  translatedNowCourse,
  profileName,
  locale,
  printerSeries,
  singleItemPrinting,
  currentItemNumber,
  totalItems,
  consolidationEnabled,
  printConfig,
  sortConfig,
  groupingConfig,
  categories,
  notifierConfig,
  allProfiles,
}) => {
  let printableItems = [] as OrderItemsPrintDetails[];
  const shouldPrintProfile =
    session.device?.deviceProfile?.showPrinterProfileInDocket || false;
  const seatManagementEnabled =
    (session?.enableSeatManagement && docket.orderType.name === 'Dine In') ||
    false;

  let notifiersHeader: string | undefined = undefined;

  const notifierOrderItems: OrderItemsPrintDetails[] = [];
  const printInRedConfig = generatePrintInRedConfig(printConfig);

  const enableCourses = printCourse(docket);

  const groupingStrategy = getGroupingStrategy(groupingConfig);
  const style = getGroupingStyle(groupingConfig);

  if (notifierConfig && notifierConfig.enabled === true) {
    const notifiers = docket.notifierItems;
    if (notifiers && notifiers?.length > 0) {
      notifiersHeader = NOTIFIERS_HEADER;
      const notifierStyle =
        notifierConfig.displayStyle || NotifierDisplayStyle.SHOW_FULL_CONTENT;
      notifiers.forEach(notifier => {
        const profileName = allProfiles?.find(
          p => p.id === notifier.printerProfileId,
        )?.name;
        if (notifierStyle === NotifierDisplayStyle.SHOW_FULL_CONTENT) {
          notifierOrderItems.push({
            header: profileName,
            groupedItems: getOrderItemsPrintString(
              [
                {
                  header: profileName,
                  printItems: notifier.items,
                },
              ],
              locale,
              template,
              printInRedConfig,
              sortConfig,
              style,
              false,
            ),
          });
        } else if (notifierStyle === NotifierDisplayStyle.SHOW_SUMMARY) {
          const itemCount = notifier.items.reduce(
            (acc, e) => acc + (e.quantity || 0),
            0,
          );
          notifierOrderItems.push({
            header: `${profileName} (${itemCount})`,
            groupedItems: [],
          });
        }
      });
    }
  }

  const getHeaderSection = (header: string | undefined, printRed = false) => {
    return {
      header:
        style === GroupDisplayStyle.HEADING
          ? header
            ? header?.toLocaleUpperCase() + '\n'
            : undefined
          : undefined,
      showHeaderDivider:
        style === GroupDisplayStyle.HEADING ||
        style === GroupDisplayStyle.SEPARATOR,
      printHeaderInRed: printRed,
    };
  };

  switch (groupingStrategy) {
    case GroupBy.COURSE:
      const itemsWithCourse = enableCourses
        ? groupByCourses(printItems, translatedNowCourse)
        : [{ header: undefined, printItems }];

      itemsWithCourse.forEach(item => {
        const items = consolidationEnabled
          ? consolidateOrderItems(item.printItems)
          : item.printItems;

        const groupedItems = seatManagementEnabled
          ? groupBySeats(items)
          : [{ header: undefined, printItems: items }];

        const printItemsString = getOrderItemsPrintString(
          groupedItems,
          locale,
          template,
          printInRedConfig,
          sortConfig,
          style,
          false,
          false,
          printerSeries,
        );

        const headerSection = getHeaderSection(
          item?.header?.toLocaleUpperCase(),
          item.printHeaderInRed,
        );

        printableItems.push({
          groupedItems: printItemsString,
          ...headerSection,
        });
      });
      break;

    case GroupBy.SEAT:
      const itemsWithSeats = seatManagementEnabled
        ? groupBySeats(printItems)
        : [{ header: undefined, printItems }];

      itemsWithSeats.forEach(item => {
        const items = consolidationEnabled
          ? consolidateOrderItems(item.printItems)
          : item.printItems;

        const groupedItems = enableCourses
          ? groupByCourses(items, translatedNowCourse)
          : [{ header: undefined, printItems: items }];

        const printItemsString = getOrderItemsPrintString(
          groupedItems,
          locale,
          template,
          printInRedConfig,
          sortConfig,
          style,
          false,
          enableCourses,
          printerSeries,
        );
        const headerSection = getHeaderSection(
          item.header?.toLocaleUpperCase(),
        );
        printableItems.push({
          groupedItems: printItemsString,
          ...headerSection,
        });
      });
      break;

    case GroupBy.CATEGORY:
      if (categories) {
        const itemsByCategory = groupByCategory(printItems, categories);
        itemsByCategory.forEach(item => {
          const items = consolidationEnabled
            ? consolidateOrderItems(item.printItems)
            : item.printItems;
          const groupedItems = enableCourses
            ? groupByCourses(items, translatedNowCourse)
            : [{ header: undefined, printItems: items }];

          const printItemsString = getOrderItemsPrintString(
            groupedItems,
            locale,
            template,
            printInRedConfig,
            sortConfig,
            style,
            true,
            enableCourses,
            printerSeries,
          );

          const headerSection = getHeaderSection(
            item?.header?.toLocaleUpperCase(),
          );
          printableItems.push({
            groupedItems: printItemsString,
            ...headerSection,
          });
        });
      }

      break;
    default:
      const items = [{ header: undefined, printItems }];
      items.forEach(item => {
        const items = consolidationEnabled
          ? consolidateOrderItems(item.printItems)
          : item.printItems;

        const groupedItems = [{ header: undefined, printItems: items }];

        const printItemsString = getOrderItemsPrintString(
          groupedItems,
          locale,
          template,
          printInRedConfig,
          sortConfig,
          style,
          true,
          enableCourses,
          printerSeries,
        );
        const headerSection = getHeaderSection(undefined);
        printableItems.push({
          groupedItems: printItemsString,
          ...headerSection,
        });
      });
  }

  if (printableItems.length === 0) {
    const finalItems = consolidationEnabled
      ? consolidateOrderItems(printItems)
      : printItems;

    const headerSection = getHeaderSection('');
    printableItems = [
      {
        groupedItems: getOrderItemsPrintString(
          [
            {
              header: DEFAULT_TABLE_ABBREVIATION,
              printItems: finalItems,
            },
          ],
          locale,
          template,
          printInRedConfig,
          sortConfig,
          style,
          false,
          false,
          printerSeries,
        ),
        ...headerSection,
      },
    ];
  }

  if (singleItemPrinting) {
    return getPrintableBufferForSingleItemLabelPrinting({
      ...(shouldPrintProfile ? { profileName: profileName || '' } : {}),
      orderItems: printableItems,
      originalDocket: docket,
      printerSeries: printerSeries || PrinterSeries.TM_M30II,
      currentItemNumber,
      totalItems,
      notifiersHeader,
      notifierOrderItems,
    });
  }
  return getPrintableBuffer({
    ...(shouldPrintProfile ? { profileName: profileName || '' } : {}),
    orderItems: printableItems,
    originalDocket: docket,
    template,
    title: hasVoidOrderItem
      ? 'CANCELLATION'
      : docket.isEdited
      ? 'EDIT ORDER'
      : 'NEW ORDER',
    printerSeries: printerSeries || PrinterSeries.TM_M30II,
    session,
    notifiersHeader,
    notifierOrderItems,
    printAllergensInRed: printInRedConfig.allergens,
  });
};

/**
 * ========================================================================================
 * Utilities for Partial orders (partial order items)
 * ========================================================================================
 *
 */

/**
 * Get product name (for all types of events)
 *
 * return newly added ot modified variants or notes
 * @param item
 * @param events
 */
// FIXME: once modifier events have been added here
export const getProductDetails = (item: DocketItem, locale?: LOCALE) => {
  locale = locale ? locale : LOCALE.ENGLISH_US;
  //const variant = item.variant;
  const product = item.product;
  return product?.name;
};

export const appendModifiers = (
  acc: PrintableProductRow[],
  item: DocketItem,
  locale?: LOCALE,
  printModifierInRed = false,
  printRemovalInRed = false,
  isComboModifier = false,
) => {
  if (item.modifiers && item.modifiers?.length > 0) {
    item.modifiers.forEach(modifier => {
      const printQuantity = modifier.quantity || 1;
      const name = modifier?.name;
      const isRemoval =
        modifier.isDefault === true && isDeselectDefaultOption(name);

      const shouldPrintInRed =
        (isRemoval && printRemovalInRed) || (!isRemoval && printModifierInRed);
      const rawName = name || '';

      let firstColumn = '';
      if (printQuantity === 1) {
        firstColumn = isRemoval ? '' : '+';
      } else {
        firstColumn = `${printQuantity}`;
      }

      let finalName = rawName;
      if (isComboModifier) {
        firstColumn = '';
        finalName =
          printQuantity > 1 ? `${printQuantity} x ${rawName}` : rawName;
      }

      acc.push([firstColumn, finalName, shouldPrintInRed]);
    });
  }
};

/**
 * Converts modifier rows to printable modifiers with table formatting for combo items
 */
const printableModifiersForComboItems = (
  modifierRows: PrintableProductRow[],
  template?: PrinterTemplate,
  printerSeries?: PrinterSeries,
): PrintableModifier[] => {
  const config = tableOrderItemModifierConfig(template);
  const firstColumnWidth = config.columns?.[0]?.width;
  const secondColumnWidth = config.columns?.[1]?.width;
  if (
    template?.name !== LRG_KITCHEN_DOCKET &&
    printerSeries === PrinterSeries.TM_U220 &&
    firstColumnWidth &&
    secondColumnWidth
  ) {
    config.columns = {
      ...config.columns,
      0: {
        ...config.columns?.[0],
        width: (firstColumnWidth as number) - 2,
      },
      1: {
        ...config.columns?.[1],
        width: (secondColumnWidth as number) - 4,
      },
    };
  }
  return modifierRows.map(([firstColumn, secondColumn, shouldPrintInRed]) => ({
    text: table([[`  ${firstColumn || ''}`, secondColumn || '']], config),
    printInRed: shouldPrintInRed === true,
  }));
};

/**
 * Creates printable content for combo items
 */
export const createComboPrintableContent = ({
  comboItems,
  locale,
  printInRedConfig,
  printerSeries,
  template,
}: {
  comboItems: DocketItem[];
  locale?: LOCALE;
  printInRedConfig?: PrintInRedConfig;
  printerSeries?: PrinterSeries;
  template?: PrinterTemplate;
}): PrintableLineItems[] => {
  const comboPrintableContent: PrintableLineItems[] = [];

  comboItems?.forEach(comboItem => {
    let processedModifiers: PrintableModifier[] = [];
    const comboItemName = getProductDetails(comboItem, locale);
    const cItemQty = (comboItem?.quantity || 1) > 1 ? comboItem.quantity : '';

    if (comboItem.modifiers && comboItem.modifiers.length > 0) {
      const modifierRows: PrintableProductRow[] = [];
      appendModifiers(
        modifierRows,
        comboItem,
        locale,
        printInRedConfig?.modifier,
        printInRedConfig?.removal,
        true,
      );

      processedModifiers = printableModifiersForComboItems(
        modifierRows,
        template,
        printerSeries,
      );
    }

    comboPrintableContent.push({
      product: table(
        [[`${cItemQty ? cItemQty + ' ' : ''}${comboItemName ?? ''}`]],
        tableComboOrderItemConfig(printerSeries),
      ),
      note: comboItem.notes
        ? table(
            [[`** ${comboItem.notes} **`]],
            tableComboOrderItemConfig(printerSeries),
          )
        : '',
      modifiers: processedModifiers,
      printNoteInRed: printInRedConfig?.notes || false,
    });
  });

  return comboPrintableContent;
};

/**
 * Returns partial order (items) printable array
 *
 * @param partialItems
 * @param events
 */
export const getOrderItemsPrintString = (
  groupedOrderItems: GroupedDocketItems[],
  locale?: LOCALE,
  template?: PrinterTemplate,
  printInRedConfig?: PrintInRedConfig,
  sortConfig?: SortingConfig,
  groupingStyle?: GroupDisplayStyle,
  appendSeatName = false,
  printHeaderInRed = false,
  printerSeries?: PrinterSeries | undefined,
): GroupedPrintItems[] => {
  const groupedItems = sortDocketItems(groupedOrderItems, sortConfig);
  const result: GroupedPrintItems[] = [];

  Object.keys(groupedItems).forEach((cur, index) => {
    const items = groupedItems[cur];
    const groupResult: PrintableLineItems[] = [];
    items.forEach(x => {
      let product = getProductDetails(x, locale);
      const modifierRows: PrintableProductRow[] = [];
      const isVoidedItem = x.itemStatus === DocketItemStatus.VOIDED;
      const quantity = isVoidedItem ? `-${x.quantity}` : `${x.quantity}`;

      if (appendSeatName && x.seatNumber) {
        product = formatNameWithSeat(product, `S${x.seatNumber}`);
      }
      const productRow: PrintableProductRow = [
        `${quantity}`,
        product,
        undefined,
      ];
      appendModifiers(
        modifierRows,
        x,
        locale,
        printInRedConfig?.modifier,
        printInRedConfig?.removal,
      );

      const processedModifierRows = modifierRows.map(
        ([firstColumn, secondColumn, shouldPrintInRed]) => ({
          text: table(
            [[firstColumn || '', secondColumn || '']],
            tableOrderItemModifierConfig(template),
          ),
          printInRed: shouldPrintInRed === true,
        }),
      );

      // Process combo items if present
      let comboPrintableContent: PrintableLineItems[] = [];
      if (x.comboItems && x.comboItems.length > 0) {
        comboPrintableContent = createComboPrintableContent({
          comboItems: x.comboItems,
          locale,
          printInRedConfig,
          printerSeries,
          template,
        });
      }

      const printableLineItem: PrintableLineItems = {
        product: `${productRow[0]} ${productRow[1]} \n`,
        note: x?.notes
          ? table(
              [[`** ${x?.notes?.trim()} **`]],
              tableOrderItemNoteConfig(template),
            )
          : '',
        //currently double array is needed for reason and notes
        modifiers:
          processedModifierRows.length > 0 ? processedModifierRows : [],
        printNoteInRed: printInRedConfig?.notes || false,
        comboItems:
          comboPrintableContent.length > 0 ? comboPrintableContent : undefined,
      };
      groupResult.push(printableLineItem);
    });
    const showSubDivider =
      groupingStyle !== GroupDisplayStyle.HIDDEN && index !== 0;
    result.push({
      subHeader: groupingStyle === GroupDisplayStyle.HEADING ? cur : undefined,
      showSubDivider: showSubDivider,
      items: groupResult,
      printSubHeaderInRed: printHeaderInRed,
    });
  });
  return result;
};

export function getShortVersion(orderNumber: string): string {
  if (orderNumber && typeof orderNumber === 'string') {
    return orderNumber.split('-').slice(0, -1).join('-');
  }

  return '';
}

export const printCourse = (docket: Docket) => {
  const courses = docket.docketItems.filter(e => e.course != null);
  return courses.length > 0;
};

export const getOrderType = (
  docket: Docket,
  showCustomerName: boolean,
  config?: TableUserConfig,
): string => {
  if (isOnlineOrder(docket))
    return getOnlineOrderType(
      docket,
      config ? config : twoColumnLargeTextConfig,
      showCustomerName,
    );

  const orderTypeName = docket.orderType?.name.toLocaleUpperCase() || 'DINE IN';

  const orderNumber = getShortVersion(docket.orderNumber);
  const orderIdentifier =
    orderTypeName == 'DINE IN' && docket?.table?.name
      ? `Table ${docket?.table?.name}`
      : orderNumber;
  return table(
    [[orderTypeName, orderIdentifier]],
    config ? config : twoColumnLargeTextConfig,
  );
};

export const getTableDetail = (docket: Docket): string => {
  if (docket.isOnline) return getOnlineOrderDetail(docket, twoColumnConfig);
  const tableData = ['createdBy.name', 'createdByDevice.name'];

  let rowInfo: string[] = [];
  const result = tableData.reduce((acc, infoPath) => {
    const orderDetail = get(docket, infoPath);
    if (!orderDetail) return acc;
    rowInfo.push(orderDetail);
    if (rowInfo.length == 2) {
      acc.push([...rowInfo] as [string, string]);
      rowInfo = [];
    }
    return acc;
  }, [] as [string, string][]);
  if (!result.length) return '';

  return table(result, twoColumnConfig);
};
const getPrinterColumns = (
  printerSeries: PrinterSeries,
  singleItemLabelPrinting: boolean,
): number => {
  if (singleItemLabelPrinting) {
    switch (printerSeries) {
      case PrinterSeries.TM_M30II:
        return 36;
      case PrinterSeries.TM_U220:
        return 30;
      case PrinterSeries.TM_M30:
        return 30;
      default:
        return 34;
    }
  } else {
    return printerSeries !== PrinterSeries.TM_U220 ? 48 : 40;
  }
};

const getDocketTimeStamp = (originalDocket: Docket): number | undefined => {
  return originalDocket.salesChannel?.name?.toLowerCase() == 'online' ||
    originalDocket.integrationInfo?.app === IntegrationApps.OOLIO_STORE
    ? originalDocket.placedAt || originalDocket.updatedAt
    : originalDocket.updatedAt;
};

const getTokenNumber = (originalDocket: Docket): string | undefined => {
  if (originalDocket.orderType?.name === 'Dine In') {
    return undefined;
  }
  const token = originalDocket.tokenNumber;
  if (!token || token === 0 || !Number.isInteger(token)) {
    return undefined;
  }
  return token.toString();
};

export const getPrintableBufferForSingleItemLabelPrinting = (args: {
  originalDocket: Docket;
  orderItems: OrderItemsPrintDetails[];
  printerSeries: PrinterSeries;
  currentItemNumber?: number;
  totalItems?: number;
  notifiersHeader?: string;
  notifierOrderItems?: OrderItemsPrintDetails[];
}): Buffer | undefined => {
  const {
    originalDocket,
    orderItems,
    printerSeries,
    currentItemNumber,
    totalItems,
    notifiersHeader,
    notifierOrderItems,
  } = args;

  const timestamp = getDocketTimeStamp(originalDocket);

  const tokenNumber = getTokenNumber(originalDocket);

  const columns = getPrinterColumns(printerSeries, true);
  const time = format(timestamp || new Date(), 'dd MMM - HH:mm').toUpperCase();

  const title = table(
    [[time, `${currentItemNumber}/${totalItems}`]],
    twoColumnConfig,
  );

  const footer = table(
    [
      [
        originalDocket?.salesChannel?.name,
        originalDocket?.createdBy?.name ||
          originalDocket?.createdByDevice?.name,
      ],
    ],
    twoColumnConfig,
  );
  const data = {
    orderName: originalDocket.customer.name || tokenNumber,
    orderItems,
    divider: divider(columns) + '\n',
    title: title,
    orderType: getOrderType(originalDocket, false, twoColumnConfig),
    footer: footer,
    printMode: printerSeries !== PrinterSeries.TM_U220 ? 'REST' : 'U220',
    currentItemNumber: currentItemNumber,
    totalItems: totalItems,
    notifiersHeader,
    notifierOrderItems,
  };
  return EscPos.getBufferFromTemplate(
    kitchenDocketLabelTemplate,
    data,
  ) as unknown as Buffer;
};

export const getCustomerInfo = (
  docket: Docket,
): { customerName: string; customerPhone: string } => {
  const { firstName = '', lastName = '', phone = '' } = docket.customer || {};
  return {
    customerName: [firstName, lastName].filter(x => !!x).join(' '),
    customerPhone: phone || '',
  };
};

export const getPrintableBuffer = (args: {
  originalDocket: Docket;
  template: PrinterTemplate;
  orderItems: OrderItemsPrintDetails[];
  title: String;
  allergens?: AllergensKey[];
  printerSeries: PrinterSeries;
  profileName?: string;
  session: Session;
  notifiersHeader?: string;
  notifierOrderItems?: OrderItemsPrintDetails[];
  printAllergensInRed?: boolean;
}): Buffer | undefined => {
  const {
    originalDocket,
    template,
    orderItems,
    title,
    allergens = [],
    profileName,
    printerSeries,
    session,
    notifiersHeader,
    notifierOrderItems,
    printAllergensInRed,
  } = args;

  const reasonOrNote = originalDocket.notes && `** ${originalDocket.notes} **`;
  const tokenNumber = getTokenNumber(originalDocket);
  const timestamp = getDocketTimeStamp(originalDocket);

  const { customerName, customerPhone } = getCustomerInfo(originalDocket);
  const columns = getPrinterColumns(printerSeries, false);
  const data = {
    orderDetails: getTableDetail(originalDocket),
    orderName: originalDocket.customer.name || tokenNumber,
    orderItems,
    divider: divider(columns) + '\n',
    dashDivider: dashDivider(columns) + '\n',
    title: title + '\n',
    orderType: getOrderType(originalDocket, true),
    timeStamp:
      format(new Date(timestamp || new Date()), 'dd-MM-yyyy hh:mm a') + '\n',
    reasonOrNote,
    channel: getOrderChannel(originalDocket),
    printerProfile: !profileName ? profileName : profileName + '\n',
    kitchenBuzzer: session?.device?.deviceProfile?.enableKitchenBuzzer,
    ...(allergens.length && {
      allergens: allergens
        .map(a => `**ALLERGY ${ProductAllergens[a].toUpperCase()}**`)
        .join('\n'),
    }),
    customerName: customerName + '\n',
    customerPhone: customerPhone + '\n',
    printMode: printerSeries !== PrinterSeries.TM_U220 ? 'REST' : 'U220',
    notifiersHeader,
    notifierOrderItems,
    printAllergensInRed: printAllergensInRed,
  };

  const printTemplate = nameToTemplate[template.name]
    ? nameToTemplate[template.name]
    : kitchenDocketTemplate;
  return EscPos.getBufferFromTemplate(printTemplate, data) as unknown as Buffer;
};
